import React, { useState, useMemo } from "react";
import { Box, Tab, Tabs, Typography, useTheme, styled, Chip, Grid, Divider } from "@mui/material";
import { AssessmentOutlined, Business, ShowChart, TrendingUp } from "@mui/icons-material";
import { RiskCategoryExposure, InsurerExposure } from "api/api-risk-dashboard.d";
import RiskCategoryPieChart from "./RiskCategoryPieChart";
import InsurerExposurePieChart from "./InsurerExposurePieChart";
import KYCExposureLineChart from "./KYCExposureLineChart";

interface ExposureTabbedCardProps {
  riskCategoryData?: RiskCategoryExposure;
  isRiskCategoryLoading?: boolean;
  riskCategoryError?: any;
  insurerData?: InsurerExposure;
  isInsurerLoading?: boolean;
  insurerError?: any;
  kycData?: any;
  isKycLoading?: boolean;
  kycError?: any;
}

// Styled components for tabs
const StyledTabs = styled(Tabs)(({ theme }) => ({
  '& .MuiTabs-indicator': {
    backgroundColor: theme.palette.primary.main,
    height: 3,
    transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
  },
  borderBottom: `1px solid ${theme.palette.divider}`,
  position: 'relative',
  zIndex: 2,
  backgroundColor: theme.palette.background.default,
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 'bold',
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
  transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
  '&.Mui-selected': {
    color: theme.palette.primary.main,
    transform: 'scale(1.05)',
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },
  '&:hover': {
    color: theme.palette.primary.main,
    opacity: 0.8,
    backgroundColor: theme.palette.action.hover,
  },
  '&.Mui-focusVisible': {
    backgroundColor: theme.palette.action.selected,
  },
  minHeight: '40px',
  padding: '0 8px',
  borderTopLeftRadius: '4px',
  borderTopRightRadius: '4px',
}));

// TabPanel component
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`exposure-tabpanel-${index}`}
      aria-labelledby={`exposure-tab-${index}`}
      {...other}
      style={{ 
        display: value === index ? 'block' : 'none',
        height: 'auto',
        width: '100%'
      }}
    >
      <Box 
        sx={{ 
          width: '100%',
          animation: value === index ? 'fadeIn 0.5s ease-in-out' : 'none',
          '@keyframes fadeIn': {
            '0%': { opacity: 0 },
            '100%': { opacity: 1 }
          }
        }}
      >
        {children}
      </Box>
    </div>
  );
}

const ExposureTabbedCard: React.FC<ExposureTabbedCardProps> = ({
  riskCategoryData,
  isRiskCategoryLoading = false,
  riskCategoryError = null,
  insurerData,
  isInsurerLoading = false,
  insurerError = null,
  kycData = null,
  isKycLoading = false,
  kycError = null
}) => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);

  // Add function to handle preventing navigation when interacting with tabs  
  const handleTabAreaClick = (event: React.MouseEvent) => {
    // Just for precaution in case this component is used in a clickable container
    event.stopPropagation();
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Process risk category data from API
  const riskCategoryMetrics = useMemo(() => {
    if (!riskCategoryData?.data || !riskCategoryData.data["risk-category"]) {
      return {
        totalCount: 0,
        highRiskCount: 0,
        mediumRiskCount: 0,
        lowRiskCount: 0,
        highRiskPercentage: "0%",
        mediumRiskPercentage: "0%",
        lowRiskPercentage: "0%"
      };
    }

    const riskCategories = riskCategoryData.data["risk-category"];
    const totalCount = riskCategories.reduce((sum, item) => sum + item.count, 0);
    
    // Find counts by risk level
    const highRiskCount = riskCategories.find((item) => item.risk_level === "High")?.count || 0;
    const mediumRiskCount = riskCategories.find((item) => item.risk_level === "Medium")?.count || 0;
    const lowRiskCount = riskCategories.find((item) => item.risk_level === "Low")?.count || 0;
    
    // Calculate percentages
    const highRiskPercentage = totalCount > 0 ? `${((highRiskCount / totalCount) * 100).toFixed(1)}%` : "0%";
    const mediumRiskPercentage = totalCount > 0 ? `${((mediumRiskCount / totalCount) * 100).toFixed(1)}%` : "0%";
    const lowRiskPercentage = totalCount > 0 ? `${((lowRiskCount / totalCount) * 100).toFixed(1)}%` : "0%";
    
    return {
      totalCount,
      highRiskCount,
      mediumRiskCount,
      lowRiskCount,
      highRiskPercentage,
      mediumRiskPercentage,
      lowRiskPercentage
    };
  }, [riskCategoryData]);

  // Process insurer data from API
  const insurerMetrics = useMemo(() => {
    if (!insurerData?.data || !Array.isArray(insurerData.data) || insurerData.data.length === 0) {
      return {
        totalCount: 0,
        topInsurer: "N/A",
        topInsurerCount: 0,
        topInsurerPercentage: "0%",
        uninsuredCount: 0
      };
    }
    
    const insurers = insurerData.data;
    const totalCount = insurers.reduce((sum, item) => sum + item.count, 0);
    
    // Find top insurer
    const sortedInsurers = [...insurers].sort((a, b) => b.count - a.count);
    const topInsurer = sortedInsurers[0];
    const topInsurerPercentage = totalCount > 0 
      ? `${((topInsurer.count / totalCount) * 100).toFixed(1)}%` 
      : "0%";
    
    // Find uninsured (if exists)
    const uninsuredEntry = insurers.find((item) => 
      item.insurer_name === "None" || 
      item.insurer_name === "Uninsured" || 
      item.insurer_name === "N/A"
    );
    const uninsuredCount = uninsuredEntry?.count || 0;
    
    return {
      totalCount,
      topInsurer: topInsurer.insurer_name,
      topInsurerCount: topInsurer.count,
      topInsurerPercentage,
      uninsuredCount
    };
  }, [insurerData]);

  // Function to render KPI chips for each tab
  const renderKpiChips = (tabIndex: number) => {
    switch (tabIndex) {
      case 0: // Risk Category
        return (
          <Grid container spacing={1} sx={{ mb: 1 }}>
            <Grid item xs={6} sm={6} md={3}>
              <Chip 
                icon={<AssessmentOutlined fontSize="small" />} 
                label={`Total: ${riskCategoryMetrics.totalCount}`}
                size="small"
                color="primary"
                sx={{ 
                  width: '100%', 
                  '& .MuiChip-label': { 
                    fontSize: { xs: '0.65rem', sm: '0.75rem' },
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }
                }}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <Chip 
                icon={<TrendingUp fontSize="small" />} 
                label={`High: ${riskCategoryMetrics.highRiskPercentage}`}
                size="small"
                color="error"
                sx={{ 
                  width: '100%', 
                  '& .MuiChip-label': { 
                    fontSize: { xs: '0.65rem', sm: '0.75rem' },
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }
                }}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <Chip 
                label={`Medium: ${riskCategoryMetrics.mediumRiskPercentage}`}
                size="small"
                color="warning"
                sx={{ 
                  width: '100%', 
                  '& .MuiChip-label': { 
                    fontSize: { xs: '0.65rem', sm: '0.75rem' },
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }
                }}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <Chip 
                label={`Low: ${riskCategoryMetrics.lowRiskPercentage}`}
                size="small"
                color="success"
                sx={{ 
                  width: '100%', 
                  '& .MuiChip-label': { 
                    fontSize: { xs: '0.65rem', sm: '0.75rem' },
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }
                }}
              />
            </Grid>
          </Grid>
        );
      
      case 1: // Insurer
        return (
          <Grid container spacing={1} sx={{ mb: 1 }}>
            <Grid item xs={6} sm={6} md={3}>
              <Chip 
                icon={<Business fontSize="small" />} 
                label={`Total: ${insurerMetrics.totalCount}`}
                size="small"
                color="primary"
                sx={{ 
                  width: '100%', 
                  '& .MuiChip-label': { 
                    fontSize: { xs: '0.65rem', sm: '0.75rem' },
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }
                }}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <Chip 
                label={`Uninsured: ${insurerMetrics.uninsuredCount}`}
                size="small"
                color="warning"
                sx={{ 
                  width: '100%', 
                  '& .MuiChip-label': { 
                    fontSize: { xs: '0.65rem', sm: '0.75rem' },
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <Chip 
                label={`Top: ${insurerMetrics.topInsurer} (${insurerMetrics.topInsurerPercentage})`}
                size="small"
                color="success"
                sx={{ 
                  width: '100%', 
                  '& .MuiChip-label': { 
                    fontSize: { xs: '0.65rem', sm: '0.75rem' },
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }
                }}
              />
            </Grid>
          </Grid>
        );
      
      case 2: // KYC
        return (
          <Grid container spacing={1} sx={{ mb: 1 }}>
            <Grid item xs={12} sm={12} md={12}>
              <Chip 
                icon={<ShowChart fontSize="small" />} 
                label="KYC Exposure Monthly Trends"
                size="small"
                color="primary"
                sx={{ 
                  width: '100%', 
                  '& .MuiChip-label': { 
                    fontSize: { xs: '0.65rem', sm: '0.75rem' },
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }
                }}
              />
            </Grid>
          </Grid>
        );
      
      default:
        return null;
    }
  };

  return (
    <Box 
      sx={{
        backgroundColor: theme.palette.background.paper,
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
        height: 'auto',
        minHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: "transform 0.3s ease, box-shadow 0.3s ease",
        position: 'relative',
        "&:hover": {
          transform: "translateY(-5px)",
          boxShadow: "0 10px 20px rgba(0, 0, 0, 0.15)",
        }
      }}
    >
      {/* Title bar with gradient background */}
      <Box 
        sx={{ 
          p: 0.75, 
          background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.error.main} 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Typography 
          variant="subtitle1" 
          sx={{ 
            fontWeight: 'bold', 
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5
          }}
        >
          <AssessmentOutlined fontSize="small" /> 
          Exposure Analysis
        </Typography>
        <Box 
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            borderRadius: '16px',
            padding: '2px 6px',
            fontSize: '0.7rem',
            fontWeight: 'bold',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            animation: 'pulse 2s infinite',
            '@keyframes pulse': {
              '0%': {
                boxShadow: '0 0 0 0 rgba(255, 255, 255, 0.4)'
              },
              '70%': {
                boxShadow: '0 0 0 6px rgba(255, 255, 255, 0)'
              },
              '100%': {
                boxShadow: '0 0 0 0 rgba(255, 255, 255, 0)'
              }
            }
          }}
        >
          NEW
        </Box>
      </Box>
      
      {/* Tabs */}
      <StyledTabs 
        value={tabValue} 
        onChange={handleTabChange} 
        aria-label="exposure tabs"
        variant="fullWidth"
        onClick={handleTabAreaClick}
      >
        <StyledTab 
          icon={<AssessmentOutlined fontSize="small" />} 
          iconPosition="start" 
          label="Risk Category" 
          id="exposure-tab-0"
          aria-controls="exposure-tabpanel-0"
        />
        <StyledTab 
          icon={<Business fontSize="small" />} 
          iconPosition="start" 
          label="Insurer" 
          id="exposure-tab-1"
          aria-controls="exposure-tabpanel-1"
        />
        <StyledTab 
          icon={<ShowChart fontSize="small" />} 
          iconPosition="start" 
          label="KYC" 
          id="exposure-tab-2"
          aria-controls="exposure-tabpanel-2"
        />
      </StyledTabs>
      
      {/* Tab panels */}
      <Box 
        sx={{ 
          px: 1.5, 
          pb: 2, 
          position: 'relative', 
          minHeight: '320px',
          height: 'auto',
          pt: 1 
        }}
        onClick={handleTabAreaClick}
      >
        {/* First tab content - Risk Category */}
        <Box 
          sx={{ 
            position: tabValue === 0 ? 'relative' : 'absolute',
            top: tabValue === 0 ? 'auto' : 8,
            left: tabValue === 0 ? 'auto' : 12,
            right: tabValue === 0 ? 'auto' : 12,
            bottom: tabValue === 0 ? 'auto' : 16,
            opacity: tabValue === 0 ? 1 : 0,
            visibility: tabValue === 0 ? 'visible' : 'hidden',
            transition: 'opacity 0.3s ease-in-out',
            pointerEvents: tabValue === 0 ? 'auto' : 'none',
            zIndex: tabValue === 0 ? 1 : 0,
            width: '100%',
            display: 'flex',
            flexDirection: 'column'
          }}
          onClick={handleTabAreaClick}
        >
          {renderKpiChips(0)}
          <Divider sx={{ mb: 1 }} />
          <Box sx={{ 
            height: { xs: 200, sm: 220, md: 240, lg: 250 }, 
            width: '100%'
          }}>
            <RiskCategoryPieChart 
              riskCategoryData={riskCategoryData as any}
              isLoading={isRiskCategoryLoading}
              error={riskCategoryError}
              showTitle={false}
            />
          </Box>
        </Box>
        
        {/* Second tab content - Insurer */}
        <Box 
          sx={{ 
            position: tabValue === 1 ? 'relative' : 'absolute',
            top: tabValue === 1 ? 'auto' : 8,
            left: tabValue === 1 ? 'auto' : 12,
            right: tabValue === 1 ? 'auto' : 12,
            bottom: tabValue === 1 ? 'auto' : 16,
            opacity: tabValue === 1 ? 1 : 0,
            visibility: tabValue === 1 ? 'visible' : 'hidden',
            transition: 'opacity 0.3s ease-in-out',
            pointerEvents: tabValue === 1 ? 'auto' : 'none',
            zIndex: tabValue === 1 ? 1 : 0,
            width: '100%',
            display: 'flex',
            flexDirection: 'column'
          }}
          onClick={handleTabAreaClick}
        >
          {renderKpiChips(1)}
          <Divider sx={{ mb: 1 }} />
          <Box sx={{ 
            height: { xs: 200, sm: 220, md: 240, lg: 250 }, 
            width: '100%'
          }}>
            <InsurerExposurePieChart 
              insurerData={insurerData}
              isLoading={isInsurerLoading}
              error={insurerError}
              showTitle={false}
            />
          </Box>
        </Box>
        
        {/* Third tab content - KYC */}
        <Box 
          sx={{ 
            position: tabValue === 2 ? 'relative' : 'absolute',
            top: tabValue === 2 ? 'auto' : 8,
            left: tabValue === 2 ? 'auto' : 12,
            right: tabValue === 2 ? 'auto' : 12,
            bottom: tabValue === 2 ? 'auto' : 16,
            opacity: tabValue === 2 ? 1 : 0,
            visibility: tabValue === 2 ? 'visible' : 'hidden',
            transition: 'opacity 0.3s ease-in-out',
            pointerEvents: tabValue === 2 ? 'auto' : 'none',
            zIndex: tabValue === 2 ? 1 : 0,
            width: '100%',
            display: 'flex',
            flexDirection: 'column'
          }}
          onClick={handleTabAreaClick}
        >
          {renderKpiChips(2)}
          <Divider sx={{ mb: 1 }} />
          <Box sx={{ 
            height: { xs: 200, sm: 220, md: 240, lg: 250 }, 
            width: '100%'
          }}>
            <KYCExposureLineChart 
              kycData={kycData}
              isLoading={isKycLoading}
              error={kycError}
              showTitle={false}
            />
          </Box>
        </Box>
      </Box>
      
      {/* Decorative bottom border */}
      <Box 
        sx={{ 
          position: 'absolute', 
          bottom: 0, 
          left: 0, 
          right: 0, 
          height: '4px', 
          background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.error.main} 100%)` 
        }} 
      />
    </Box>
  );
};

export default ExposureTabbedCard;
